import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, CalendarDays, Check, Clock, MapPin, Mountain, Route, Sun, Users } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import AvailabilityCalendar from "../components/AvailabilityCalendar";
import { MaskedLine, Reveal } from "../components/Motion";

function BookingForm({ hike }) {
  const [form, setForm] = useState({ preferred_date: "", people: 2, name: "", email: "", message: "", time_pref: "am" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);
  const [blockedDates, setBlockedDates] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [participants, setParticipants] = useState([]);

  useEffect(() => {
    api.get("/availability")
      .then((r) => {
        setBlockedDates(r.data.blocked || []);
        setPeriods(r.data.periods || []);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    setParticipants((prev) => {
      const need = Math.max(0, Number(form.people) - 1);
      const next = prev.slice(0, need);
      while (next.length < need) next.push({ name: "", email: "", proxy: false });
      return next;
    });
  }, [form.people]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const halfDay = parseInt(hike.duration, 10) < 4;
  const dayEntries = blockedDates.filter((e) => e.date === form.preferred_date);
  const taken = new Set(dayEntries.map((e) => e.slot));
  const dateBlocked =
    taken.has("full") ||
    (!halfDay && dayEntries.length > 0) ||
    (halfDay && taken.has(form.time_pref));
  const datePartial = !dateBlocked && halfDay && dayEntries.length > 0;
  const fmtDay = (iso) => new Date(iso + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  const rangesText = periods.map((p) => `${fmtDay(p.start)} – ${fmtDay(p.end)}`).join("  ·  ");
  const outOfSeason =
    periods.length > 0 &&
    !!form.preferred_date &&
    !periods.some((p) => p.start <= form.preferred_date && form.preferred_date <= p.end);

  function pickDate(iso) {
    let pref = form.time_pref;
    const slots = new Set(blockedDates.filter((e) => e.date === iso).map((e) => e.slot));
    if (halfDay && (slots.has("full") || slots.has(pref))) pref = pref === "am" ? "pm" : "am";
    setForm({ ...form, preferred_date: iso, time_pref: pref });
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.preferred_date) {
      toast.error("Please pick a date on the calendar");
      return;
    }
    if (dateBlocked || outOfSeason) return;
    setSending(true);
    try {
      const { data } = await api.post("/bookings", {
        ...form,
        people: Number(form.people),
        hike_slug: hike.slug,
        time_pref: halfDay ? form.time_pref : null,
        participants: [
          { name: form.name, email: form.email, proxy: false },
          ...participants.map((p) => ({ name: p.name, email: p.email || null, proxy: !!p.proxy })),
        ],
      });
      setDone(data.id);
      toast.success("Booking request sent");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  }

  if (done) {
    return (
      <div data-testid="booking-success" className="rounded-3xl border border-olive/30 bg-olive-pale p-8">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-olive text-paper">
          <Check size={22} />
        </span>
        <h3 className="mt-6 font-serif text-3xl tracking-tight">Request sent.</h3>
        <p className="mt-4 text-base leading-relaxed text-ink/70">
          Vasilis will personally review your date — usually within a day. Once he approves, every participant
          receives an email with a personal waiver link to sign (health disclosure + liability release).
          When everyone has signed, the booking is confirmed and payment details follow.
        </p>
        <p className="mt-4 rounded-xl border border-terra/30 bg-terra/5 px-4 py-3 text-base leading-relaxed text-ink/75" data-testid="booking-last-minute">
          <strong>For last minute bookings contact Vasilis directly:</strong> +30 6976 231 058
        </p>
        <p className="mt-5 text-sm uppercase tracking-[0.2em] text-ink/50">
          Reference · <span data-testid="booking-reference">{done.slice(0, 8).toUpperCase()}</span>
        </p>
      </div>
    );
  }

  const inputCls =
    "w-full rounded-xl border border-ink/20 bg-white px-4 py-3.5 text-lg text-ink outline-none transition-[border-color,box-shadow] duration-300 placeholder:text-ink/35 focus:border-terra focus:ring-2 focus:ring-terra/20";

  return (
    <form onSubmit={submit} data-testid="booking-form" className="rounded-3xl border border-ink/15 bg-white p-7 sm:p-9">
      <h3 className="font-serif text-3xl tracking-tight">Request this hike</h3>
      <p className="mt-2 text-base text-ink/60">No payment now — Vasilis confirms availability first.</p>

      <div className="mt-7">
        <span className="mb-2 flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-ink/60">
          <CalendarDays size={13} /> Preferred date
        </span>
        <AvailabilityCalendar
          periods={periods}
          blocked={blockedDates}
          halfDay={halfDay}
          selected={form.preferred_date}
          onSelect={pickDate}
        />
        {form.preferred_date && !dateBlocked && !outOfSeason && (
          <p className="mt-3 rounded-xl bg-terra/10 px-4 py-2.5 text-base font-medium text-terra" data-testid="booking-selected-date">
            {fmtDay(form.preferred_date)}
          </p>
        )}
        {periods.length > 0 && !outOfSeason && (
          <p data-testid="booking-season-note" className="mt-2 text-sm text-ink/50">
            Vasilis operates: {rangesText}
          </p>
        )}
        {outOfSeason && (
          <p data-testid="booking-out-of-season" className="mt-2 rounded-xl bg-red-50 px-4 py-2.5 text-base text-red-700">
            Vasilis isn't available on that date — he operates {rangesText}. Please choose a date within one of these periods.
          </p>
        )}
        {dateBlocked && (
          <p data-testid="booking-date-blocked" className="mt-2 rounded-xl bg-red-50 px-4 py-2.5 text-base text-red-700">
            {halfDay
              ? "That half of the day is taken — try the other half or another date."
              : "Vasilis is already booked on this day — please pick another date."}
          </p>
        )}
        {datePartial && (
          <p data-testid="booking-date-partial" className="mt-2 rounded-xl bg-amber-50 px-4 py-2.5 text-base text-amber-800">
            The other half of this day is booked — your chosen time is free.
          </p>
        )}
      </div>

      {halfDay && (
        <div className="mt-5">
          <span className="mb-2 flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-ink/60">
            <Clock size={13} /> Morning or afternoon?
          </span>
          <div className="grid grid-cols-2 gap-3">
            {[["am", "Morning"], ["pm", "Afternoon"]].map(([v, label]) => {
              const takenHalf = !!form.preferred_date && (taken.has("full") || taken.has(v));
              return (
                <button
                  type="button"
                  key={v}
                  disabled={takenHalf}
                  onClick={() => setForm({ ...form, time_pref: v })}
                  data-testid={`time-pref-${v}`}
                  className={`rounded-xl border px-4 py-3.5 text-lg transition-[border-color,background-color,color,opacity] duration-300 ${
                    takenHalf
                      ? "border-ink/10 bg-ink/[0.03] text-ink/30 line-through"
                      : form.time_pref === v
                        ? "border-terra bg-terra/10 font-medium text-terra"
                        : "border-ink/20 bg-white text-ink/70 hover:border-ink/40"
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>
      )}

      <label className="mt-5 block">
        <span className="mb-2 flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-ink/60">
          <Users size={13} /> People (1–20)
        </span>
        <select value={form.people} onChange={set("people")} data-testid="booking-people-select" className={inputCls}>
          {Array.from({ length: 20 }, (_, i) => i + 1).map((n) => (
            <option key={n} value={n}>{n} {n === 1 ? "person" : "people"}</option>
          ))}
        </select>
      </label>

      <div className="mt-5 rounded-2xl border border-ink/10 bg-olive-pale/40 p-5" data-testid="participants-section">
        <span className="mb-1 block text-sm uppercase tracking-[0.2em] text-ink/60">Who's coming?</span>
        <p className="mb-4 text-sm leading-relaxed text-ink/55">
          Every participant gets a personal waiver link by email — the booking is confirmed once everyone has signed.
        </p>
        <div className="mb-3 grid gap-2 sm:grid-cols-2" data-testid="participant-lead">
          <input type="text" required minLength={2} placeholder="Participant 1 — full name (you)" value={form.name}
            onChange={set("name")} data-testid="booking-name-input" className={inputCls} />
          <input type="email" required placeholder="Email address" value={form.email}
            onChange={set("email")} data-testid="booking-email-input" className={inputCls} />
        </div>
        {participants.map((p, i) => (
          <div key={i} className="mb-3">
            <div className="grid gap-2 sm:grid-cols-2">
              <input type="text" required minLength={2} placeholder={`Participant ${i + 2} — full name`} value={p.name}
                onChange={(e) => setParticipants(participants.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                data-testid={`participant-name-${i + 2}`} className={inputCls} />
              <input type="email" required={!p.proxy}
                placeholder={p.proxy ? "Email optional — no waiver email sent" : "Email address"} value={p.email}
                onChange={(e) => setParticipants(participants.map((x, j) => (j === i ? { ...x, email: e.target.value } : x)))}
                data-testid={`participant-email-${i + 2}`} className={inputCls} />
            </div>
            <label className="mt-2 flex cursor-pointer items-start gap-2.5 text-sm leading-relaxed text-ink/60">
              <input type="checkbox" checked={!!p.proxy}
                onChange={(e) => setParticipants(participants.map((x, j) => (j === i ? { ...x, proxy: e.target.checked } : x)))}
                data-testid={`participant-proxy-${i + 2}`} className="mt-0.5 h-4 w-4 shrink-0 accent-terra" />
              I will sign on behalf of this person (parent, guardian, or authorized representative)
            </label>
          </div>
        ))}
      </div>

      <label className="mt-5 block">
        <span className="mb-2 block text-sm uppercase tracking-[0.2em] text-ink/60">Message (optional)</span>
        <textarea rows={3} value={form.message} onChange={set("message")}
          placeholder="Fitness level, questions, a birthday on the trail…"
          data-testid="booking-message-input" className={inputCls} />
      </label>

      <button
        type="submit"
        disabled={sending || dateBlocked || outOfSeason}
        data-testid="booking-submit-btn"
        className="mt-7 w-full rounded-full bg-terra py-4 text-base uppercase tracking-[0.18em] text-paper transition-[background-color,transform,opacity] duration-300 hover:-translate-y-0.5 hover:bg-terra-dark disabled:opacity-50"
      >
        {sending ? "Sending…" : dateBlocked || outOfSeason ? "Date unavailable" : "Send booking request"}
      </button>
      <p className="mt-4 text-center text-sm leading-relaxed text-ink/45">
        {hike.price} · Pay online after confirmation or in cash on the day.
      </p>
    </form>
  );
}

export default function HikeDetail() {
  const { slug } = useParams();
  const [hike, setHike] = useState(null);
  const [missing, setMissing] = useState(false);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  useEffect(() => {
    setHike(null);
    setMissing(false);
    api.get(`/hikes/${slug}`)
      .then((r) => {
        setHike(r.data);
        document.title = `${r.data.name} — Salt & Stone, Hydra`;
      })
      .catch(() => setMissing(true));
  }, [slug]);

  if (missing) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-6 px-5 pt-24 text-center" data-testid="hike-not-found">
        <h1 className="font-serif text-5xl tracking-tight">This trail doesn't exist.</h1>
        <Link to="/hikes" className="text-base uppercase tracking-[0.2em] text-terra underline underline-offset-8">
          Back to the hikes
        </Link>
      </main>
    );
  }

  if (!hike) return <main className="min-h-screen pt-24" data-testid="hike-loading" />;

  const meta = [
    { icon: Mountain, label: "Difficulty", value: hike.difficulty, tid: "difficulty" },
    { icon: Clock, label: "Duration", value: hike.duration, tid: "duration" },
    { icon: Route, label: "Distance", value: hike.distance, tid: "distance" },
    { icon: Users, label: "Group", value: hike.group_size, tid: "group-size" },
    { icon: Sun, label: "Season", value: hike.season || "All year", tid: "season" },
    { icon: MapPin, label: "Meeting point", value: hike.meeting_point || "Hydra Town harbour", note: "Other spots on request", tid: "meeting-point" },
  ];

  return (
    <main data-testid="hike-detail-page">
      <section ref={heroRef} className="relative flex h-[70svh] items-end overflow-hidden bg-deep">
        <motion.div style={{ y }} className="absolute inset-0 scale-110">
          <img src={hike.image} alt={hike.name} className="h-full w-full object-cover brightness-110 saturate-110" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-t from-deep/70 via-deep/10 to-deep/20" />
        <div className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-12 sm:px-8">
          <MaskedLine>
            <Link to="/hikes" data-testid="back-to-hikes" className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.25em] text-paper/70 transition-colors duration-300 hover:text-paper">
              <ArrowLeft size={14} /> All hikes
            </Link>
          </MaskedLine>
          <MaskedLine delay={0.15}>
            <h1 className="display-serif mt-4 max-w-3xl text-5xl leading-[0.95] text-paper sm:text-6xl lg:text-7xl" data-testid="hike-title">
              {hike.name}
            </h1>
          </MaskedLine>
        </div>
      </section>

      <section className="border-b border-ink/10">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-px bg-ink/10 px-0 sm:grid-cols-3 lg:grid-cols-6" data-testid="hike-meta-grid">
          {meta.map((m) => (
            <div key={m.tid} className="bg-paper px-5 py-6 sm:px-8" data-testid={`hike-${m.tid}`}>
              <m.icon size={17} className="text-terra" />
              <p className="mt-3 text-sm uppercase tracking-[0.2em] text-ink/45">{m.label}</p>
              <p className="mt-1 text-lg font-medium">{m.value}</p>
              {m.note && <p className="mt-1 text-sm text-ink/45">{m.note}</p>}
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl gap-14 px-5 py-20 sm:px-8 sm:py-28 lg:grid lg:grid-cols-12">
        <div className="lg:col-span-7">
          <Reveal>
            <p className="font-serif text-2xl italic leading-snug text-terra sm:text-3xl">{hike.tagline}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-7 text-lg leading-relaxed text-ink/75 sm:text-lg" data-testid="hike-description">
              {hike.description}
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <h2 className="overline-label mt-14 text-ink/50">What's included</h2>
            <ul className="mt-6 space-y-4" data-testid="hike-included-list">
              {hike.included.map((item) => (
                <li key={item} className="flex items-start gap-3 text-lg text-ink/80">
                  <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-olive text-paper">
                    <Check size={12} />
                  </span>
                  {item}
                </li>
              ))}
            </ul>
          </Reveal>

          <div className="mt-14 grid gap-5 sm:grid-cols-2">
            {hike.gallery.map((src, i) => (
              <Reveal key={src} delay={i * 0.1} className={i % 2 === 1 ? "sm:mt-12" : ""}>
                <div className="aspect-[4/5] overflow-hidden rounded-2xl">
                  <img src={src} alt={`${hike.name} — trail photograph ${i + 1}`} loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 ease-out hover:scale-105" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="mt-14 lg:col-span-5 lg:mt-0">
          <div className="lg:sticky lg:top-28">
            <Reveal delay={0.1}>
              <BookingForm hike={hike} />
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
